import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess/acls.pb';
import { hasSchemaACL } from '@frontend/auth-helpers';
import { useHasPhoneSystemAccess } from '@frontend/phone-config';
import { useAppFlagStore } from '@frontend/shared';
import { usePhoneSettingsShallowStore } from './store/settings';

const useManagementPermission = (permissionToCheck: Permission) => {
  const { settingsTenantLocation } = usePhoneSettingsShallowStore('settingsTenantLocation');
  const { getFeatureFlagValue } = useAppFlagStore();

  const orgPhoneSystemAccess = useHasPhoneSystemAccess({
    locationIds: [settingsTenantLocation?.orgId ?? ''],
  });

  // Org-level check
  const orgId = settingsTenantLocation?.orgId;
  const orgHasPermission =
    !!orgId &&
    (() => {
      const orgHasPhoneFullAccess = hasSchemaACL(orgId, Permission.PHONE_FULL_ACCESS);
      const orgHasPhoneAdminAccess = orgHasPhoneFullAccess && orgPhoneSystemAccess;
      const orgHasSpecificPermission = hasSchemaACL(orgId, permissionToCheck);

      return orgHasSpecificPermission || orgHasPhoneAdminAccess;
    })();

  return (locationId: string, precomputedPhoneSystemAccess?: boolean): boolean => {
    if (orgHasPermission) return true;

    // Location-level fallback
    const hasPhoneFullAccess = hasSchemaACL(locationId, Permission.PHONE_FULL_ACCESS);
    const hasPhoneSystemAccess = precomputedPhoneSystemAccess ?? false;
    const hasPhoneAdminAccess = hasPhoneFullAccess && hasPhoneSystemAccess;
    const hasSpecificPermission = hasSchemaACL(locationId, permissionToCheck);
    const hasNewUsersManagementFlag = getFeatureFlagValue({
      flagName: 'users-management-2.0',
      locationIds: [locationId],
    });

    return !hasNewUsersManagementFlag || hasSpecificPermission || hasPhoneAdminAccess;
  };
};

export const useOverridesManagementPermission = () => useManagementPermission(Permission.VOICEMAIL_OVERRIDE_WRITE);
export const useHoursManagementPermission = () => useManagementPermission(Permission.OFFICE_HOURS_SETTING_WRITE);
